import type { JSX } from "react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api } from "../api.js";
import { PageHeader } from "../components/PageHeader.js";
import { StatCard } from "../components/StatCard.js";
import { Money } from "../components/Money.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table.js";
import { cn } from "../lib/utils.js";

const pct = (s: string | null | undefined): string => (s == null ? "—" : `${(Number(s) * 100).toFixed(2)}%`);
const toDisplayMinor = (fourDp: number): number => Math.round(fourDp / 100);

type TabKey = "overview" | "lots" | "dividends" | "transactions" | "prices" | "news";
const TABS: { key: TabKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "lots", label: "Lots" },
  { key: "dividends", label: "Dividends" },
  { key: "transactions", label: "Transactions" },
  { key: "prices", label: "Prices" },
  { key: "news", label: "News" },
];

export function StockDetail(): JSX.Element {
  const { securityId = "" } = useParams();
  const [tab, setTab] = useState<TabKey>("overview");
  const detailQ = useQuery({ queryKey: ["stock-detail", securityId], queryFn: () => api.stockDetail(securityId) });

  const sec = detailQ.data?.security;
  const title = sec ? `${sec.symbol}${sec.name ? ` — ${sec.name}` : ""}` : "Stock";

  return (
    <>
      <PageHeader title={title}>
        <Button asChild variant="outline" size="sm"><Link to="/stocks/performance">← Performance</Link></Button>
      </PageHeader>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <Button key={t.key} variant={tab === t.key ? "default" : "ghost"} size="sm" onClick={() => { setTab(t.key); }}>
            {t.label}
          </Button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab securityId={securityId} />}
      {tab === "lots" && <LotsTab securityId={securityId} />}
      {tab === "dividends" && <TxnTab securityId={securityId} kind="dividends" />}
      {tab === "transactions" && <TxnTab securityId={securityId} kind="transactions" />}
      {tab === "prices" && <PricesTab securityId={securityId} />}
      {tab === "news" && <NewsTab securityId={securityId} />}
    </>
  );
}

function OverviewTab({ securityId }: { securityId: string }): JSX.Element {
  const detailQ = useQuery({ queryKey: ["stock-detail", securityId], queryFn: () => api.stockDetail(securityId) });
  const p = detailQ.data?.performance;
  if (!p) return <Card className="p-5 text-muted-foreground">No performance yet — run “Recompute” on the Performance page after a broker sync.</Card>;

  const eur = (v: number | null): JSX.Element => (v == null ? <>—</> : <Money amountMinor={v} native="EUR" />);
  const colored = (v: number | null): JSX.Element => (
    <span className={cn(v != null && v < 0 ? "text-bad" : "text-good")}>{eur(v)}</span>
  );

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
      <StatCard label="Total benefit" value={colored(p.total_benefit_minor)} sub="P&L + net dividends − fees" />
      <StatCard label="Return %" value={pct(p.total_return_pct)} sub="vs invested cost" />
      <StatCard label="Annualized (XIRR)" value={pct(p.xirr_pct)} sub="money-weighted" />
      <StatCard label="Yield on cost" value={pct(p.yield_on_cost_pct)} sub="TTM dividends / cost" />
      <StatCard label="Unrealized P&L" value={colored(p.unrealized_pl_minor)} />
      <StatCard label="Realized P&L" value={colored(p.realized_pl_minor)} />
      <StatCard label="Dividends (net)" value={eur(p.dividends_net_minor)} sub={<>gross {eur(p.dividends_gross_minor)}</>} />
      <StatCard label="FX effect" value={colored(p.fx_effect_minor)} sub="EUR vs local currency" />
      <StatCard label="Market value" value={eur(p.market_value_minor)} sub={`qty ${p.quantity}`} />
      <StatCard label="Cost basis" value={eur(p.cost_basis_minor)} />
      <StatCard label="Holding period" value={p.holding_days != null ? `${String(p.holding_days)} days` : "—"} />
      <StatCard label="Local vs EUR return" value={`${pct(p.local_return_pct)} / ${pct(p.eur_return_pct)}`} />
    </div>
  );
}

function LotsTab({ securityId }: { securityId: string }): JSX.Element {
  const q = useQuery({ queryKey: ["stock-lots", securityId], queryFn: () => api.stockLots(securityId) });
  const rows = q.data?.items ?? [];
  return (
    <Card className="overflow-hidden p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Opened</TableHead>
            <TableHead className="text-right">Qty</TableHead>
            <TableHead className="text-right">Remaining</TableHead>
            <TableHead className="text-right">Cost basis</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Realized</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((l) => (
            <TableRow key={l.id}>
              <TableCell>{l.open_date.slice(0, 10)}</TableCell>
              <TableCell className="text-right">{l.quantity_open}</TableCell>
              <TableCell className="text-right">{l.quantity_remaining}</TableCell>
              <TableCell className="text-right"><Money amountMinor={l.cost_basis_minor} native={l.currency} /></TableCell>
              <TableCell>{l.status}</TableCell>
              <TableCell className={cn("text-right", l.realized_pl_minor < 0 ? "text-bad" : "text-good")}>
                <Money amountMinor={l.realized_pl_minor} native={l.currency} />
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-muted-foreground py-6 text-center">No lots.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}

function TxnTab({ securityId, kind }: { securityId: string; kind: "dividends" | "transactions" }): JSX.Element {
  const q = useQuery({
    queryKey: ["stock-txns", kind, securityId],
    queryFn: () => (kind === "dividends" ? api.stockDividends(securityId) : api.stockTxns(securityId)),
  });
  const rows = q.data?.items ?? [];
  return (
    <Card className="overflow-hidden p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Dir</TableHead>
            <TableHead className="text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((t) => (
            <TableRow key={t.id}>
              <TableCell>{t.occurred_at.slice(0, 10)}</TableCell>
              <TableCell>{t.description ?? "—"}</TableCell>
              <TableCell>{t.direction}</TableCell>
              <TableCell className={cn("text-right", t.direction === "debit" ? "text-bad" : "text-good")}>
                <Money amountMinor={t.amount_minor} native={t.currency} sign={t.direction === "debit" ? "-" : "+"} />
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-muted-foreground py-6 text-center">Nothing here.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}

function NewsTab({ securityId }: { securityId: string }): JSX.Element {
  const q = useQuery({ queryKey: ["stock-news", securityId], queryFn: () => api.stockNews(securityId) });
  const rows = q.data?.items ?? [];
  if (rows.length === 0) return <Card className="p-5 text-muted-foreground">No news linked yet — run “Ingest news” on the News page.</Card>;
  return (
    <div className="flex flex-col gap-3">
      {rows.map((n) => (
        <Card key={n.id} className="gap-1 p-4">
          <a href={n.url} target="_blank" rel="noreferrer" className="text-brand font-medium hover:underline">{n.title}</a>
          <div className="text-muted-foreground flex gap-2 text-xs">
            <span>{n.source}</span>
            {n.published_at && <span>· {n.published_at.slice(0, 10)}</span>}
            {n.sentiment && <span>· {n.sentiment}</span>}
          </div>
          {n.summary && <p className="text-sm">{n.summary}</p>}
        </Card>
      ))}
    </div>
  );
}

function PricesTab({ securityId }: { securityId: string }): JSX.Element {
  const q = useQuery({ queryKey: ["stock-prices", securityId], queryFn: () => api.stockPrices(securityId) });
  const rows = q.data?.items ?? [];
  return (
    <Card className="overflow-hidden p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead className="text-right">Close</TableHead>
            <TableHead>Source</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((p) => (
            <TableRow key={p.id}>
              <TableCell>{p.price_date}</TableCell>
              <TableCell className="text-right"><Money amountMinor={toDisplayMinor(p.close_minor)} native={p.currency} /></TableCell>
              <TableCell>{p.source}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={3} className="text-muted-foreground py-6 text-center">No prices.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}
